import { useState } from 'react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createTrpcClient, isNetworkError, isUnauthorized, TRPCProvider } from '@/lib/trpc'

function createQueryClient(): QueryClient {
  // An expired or revoked session shows the sign-in screen instead of failing every request.
  const onError = (error: unknown): void => {
    if (isUnauthorized(error)) void queryClient.resetQueries()
  }
  const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        // Network errors (server restarting) retry for about half a minute; others twice.
        retry: (count, error) => !isUnauthorized(error) && count < (isNetworkError(error) ? 10 : 2),
        retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 5_000)
      }
    }
  })
  return queryClient
}

export function Providers({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [queryClient] = useState(createQueryClient)
  const [trpcClient] = useState(createTrpcClient)

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  )
}
